/**
 * RouteSkeleton — fallback for <Suspense> boundaries while lazy
 * route chunks load.
 *
 * Composes the `Skeleton` primitive from Galaxia Pro into
 * route-shape-aware silhouettes. Cumple Principio VI (skeletons del
 * DS, no inline).
 */
import type { JSX } from "react";

import { Skeleton } from "../../../../../.packages/galaxia-pro";

export type RouteSkeletonVariant = "landing" | "login" | "app" | "generic";

interface RouteSkeletonProps {
  variant?: RouteSkeletonVariant;
}

export function RouteSkeleton({
  variant = "generic",
}: RouteSkeletonProps): JSX.Element {
  if (variant === "landing") {
    return (
      <div role="status" aria-busy="true" aria-label="Cargando contenido">
        {/* Hero silhouette */}
        <div
          style={{
            padding: "var(--s-8) var(--s-5)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-5)",
            maxWidth: "var(--container-max)",
            margin: "0 auto",
          }}
        >
          <Skeleton width="80%" height="var(--fs-3xl)" />
          <Skeleton lines={2} height="var(--fs-base)" />
          <div style={{ display: "flex", gap: "var(--s-3)" }}>
            <Skeleton width={140} height={44} radius="var(--r-md)" />
            <Skeleton width={140} height={44} radius="var(--r-md)" />
          </div>
        </div>
        {/* Products grid silhouette */}
        <div
          style={{
            padding: "var(--s-8) var(--s-5)",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: "var(--s-3)",
            maxWidth: "var(--container-max)",
            margin: "0 auto",
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={200} radius="var(--r-xl)" />
          ))}
        </div>
      </div>
    );
  }
  if (variant === "login") {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Cargando login"
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          padding: "var(--s-8) var(--s-5)",
          gap: "var(--s-5)",
          maxWidth: 480,
          margin: "0 auto",
          justifyContent: "center",
        }}
      >
        <Skeleton width={56} height={56} radius="var(--r-pill)" />
        <Skeleton width="70%" height="var(--fs-2xl)" />
        <Skeleton lines={2} height="var(--fs-base)" />
        <Skeleton width="100%" height={48} radius="var(--r-md)" />
        <Skeleton width="100%" height={48} radius="var(--r-md)" />
        <Skeleton width="100%" height={48} radius="var(--r-md)" />
      </div>
    );
  }
  if (variant === "app") {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Cargando aplicación"
        style={{
          padding: "var(--s-5)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-5)",
        }}
      >
        <div
          style={{ display: "flex", gap: "var(--s-3)", alignItems: "center" }}
        >
          <Skeleton width={40} height={40} radius="var(--r-pill)" />
          <Skeleton width={180} height="var(--fs-md)" />
        </div>
        <Skeleton width="60%" height="var(--fs-xl)" />
        <Skeleton lines={3} height="var(--fs-base)" />
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Cargando"
      style={{
        padding: "var(--s-8)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--s-3)",
        maxWidth: 600,
        margin: "0 auto",
      }}
    >
      <Skeleton width="60%" height="var(--fs-xl)" />
      <Skeleton lines={3} height="var(--fs-base)" />
    </div>
  );
}

export default RouteSkeleton;
